import { useState } from "react";
import { Link } from "react-router";
import { styled } from "styled-components";

import { RightArrow, SectionTitleUnderlined, StyledModalText, UIButton } from "common/components";
import { breakpoints, createScrollbarStyle } from "common/styles";
import { useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { interpolate } from "domains/language/interpolate";
import { activateModal } from "domains/modal/actions";
import { FilterTags } from "domains/monitor/filter/components/FilterTags";
import { getTopicsFilterSelectedIds } from "domains/monitor/filter/selectors";
import { useMedia } from "domains/viewport/useMedia";
import { createTopicsSelector, getFollowedTopicsIds, getFollowedTopicsIsPending } from "entities/topics/selectors";

const StyledOuterContainer = styled.div`
  margin-top: 1rem;

  @media ${breakpoints.medium} {
    margin-top: 0;
  }
`;

const StyledRightArrow = styled(RightArrow)`
  width: 14px;
  height: 14px;
  margin-left: 10px;
`;

const StyledSeeAllButton = styled(UIButton)`
  margin-top: 10px;
`;

const StyledLink = styled(Link)`
  cursor: pointer;
  text-decoration: none;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.blue};

  &:hover {
    color: ${({ theme }) => theme.colors.blueDark};
  }
`;

const StyledContainer = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  ${createScrollbarStyle()};

  @media ${breakpoints.extraLarge} {
    max-height: 45vh;
  }
`;

export const Filter = () => {
  const dispatch = useDispatch();
  const isLarge = useMedia({ small: false, medium: false }, true);
  const items = useMemoisedSelector(getFollowedTopicsIds, createTopicsSelector);
  const isPending = useSelector(getFollowedTopicsIsPending);
  const selectedItems = useSelector(getTopicsFilterSelectedIds);
  const [numberOfItems, setNumberOfItems] = useState<number>(10);
  const translate = useTranslate();

  const toggleItems = () => setNumberOfItems(numberOfItems < items.length ? items.length : 10);

  const displayItems = isLarge ? items.slice(0, numberOfItems) : items;

  const manageTopicHandle = () =>
    dispatch(
      activateModal({
        id: "ManageTopics",
      }),
    );

  const discoverInterpolation = (discover: string) => <StyledLink to="/topics">{discover}</StyledLink>;

  const manageButton = (
    <UIButton onClick={manageTopicHandle} variant="neutral">
      {translate("app.monitor.manage")}
      <StyledRightArrow />
    </UIButton>
  );

  if (items.length === 0 && !isPending) {
    return (
      <>
        <SectionTitleUnderlined>{translate("mp.1.1.title")}</SectionTitleUnderlined>
        <StyledModalText>
          {interpolate(translate("app.monitor.filter_empty"), {
            discover: discoverInterpolation,
          })}
        </StyledModalText>
      </>
    );
  }
  return (
    <StyledOuterContainer>
      <SectionTitleUnderlined rightElement={manageButton}>{translate("app.filter.title")}</SectionTitleUnderlined>
      <StyledContainer>
        <FilterTags items={displayItems} selectedItems={selectedItems} />
        {isLarge && items.length > 10 && (
          <StyledSeeAllButton onClick={toggleItems} variant="neutral">
            {translate(numberOfItems < items.length ? "app.topic.see_all" : "app.monitor.hide_topics")}
          </StyledSeeAllButton>
        )}
      </StyledContainer>
    </StyledOuterContainer>
  );
};
